import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  Users,
  Award
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminBadge {
  id: number
  slug: string
  name: string
  emoji: string
  category: string
  points_reward: number
  community_name: string | null
  users_count: number
  created_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  category: string | null
  community_id: string | null
  global: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface IndexProps {
  badges: AdminBadge[]
  pagination: Pagination
  filters: Filters
  categories: string[]
  communities: Community[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getCategoryBadgeColor(category: string): string {
  switch (category) {
    case 'performance': return 'bg-blue-100 text-blue-800'
    case 'community': return 'bg-green-100 text-green-800'
    case 'recognition': return 'bg-yellow-100 text-yellow-800'
    case 'seniority': return 'bg-purple-100 text-purple-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function getCategoryLabel(category: string): string {
  switch (category) {
    case 'performance': return 'Performance'
    case 'community': return 'Communauté'
    case 'recognition': return 'Reconnaissance'
    case 'seniority': return 'Ancienneté'
    default: return category
  }
}

export default function Index({ badges, pagination, filters, categories, communities }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/badges', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/badges', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((badge: AdminBadge) => {
    if (confirm(`Voulez-vous vraiment supprimer le badge "${badge.name}" ?`)) {
      router.delete(`/admin/badges/${badge.slug}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Badges"
      breadcrumbs={[{ label: 'Badges' }]}
    >
      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par nom ou description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Link href="/admin/badges/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Nouveau badge
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-44">
            <Select
              value={filters.category || ''}
              onChange={(e) => applyFilters({ category: e.target.value || null })}
            >
              <option value="">Toutes catégories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {getCategoryLabel(category)}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautés</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.global === 'true'}
              onChange={(e) => applyFilters({ global: e.target.checked ? 'true' : null })}
              className="w-4 h-4 rounded border-gray-300 text-tessan-primary focus:ring-tessan-primary"
            />
            <span className="text-sm text-text-dark">Badges globaux uniquement</span>
          </label>

          {(filters.search || filters.category || filters.community_id || filters.global) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  category: null,
                  community_id: null,
                  global: null
                })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} badge{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Badges Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('name')}
                  >
                    Badge
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Catégorie
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('points_reward')}
                  >
                    Points
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Communauté
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('users_count')}
                  >
                    <Users size={14} />
                    Attribué
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Créé le
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {badges.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <Award size={32} className="text-gray-300" />
                      Aucun badge trouvé
                    </div>
                  </td>
                </tr>
              ) : (
                badges.map((badge) => (
                  <tr key={badge.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{badge.emoji}</span>
                        <div>
                          <p className="font-medium text-text-dark">{badge.name}</p>
                          <p className="text-xs text-text-gray">{badge.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeColor(badge.category)}`}>
                        {getCategoryLabel(badge.category)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {badge.points_reward > 0 ? (
                        <span className="font-medium text-green-600">+{badge.points_reward}</span>
                      ) : (
                        <span className="text-text-gray">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {badge.community_name ? (
                        <span className="text-sm text-text-dark">{badge.community_name}</span>
                      ) : (
                        <span className="text-xs text-text-gray bg-gray-100 px-2 py-0.5 rounded">Global</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Users size={14} className="text-text-gray" />
                        <span className="font-medium text-text-dark">{badge.users_count}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {formatDate(badge.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/admin/badges/${badge.slug}`}>
                          <Button variant="secondary" className="p-2">
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Link href={`/admin/badges/${badge.slug}/edit`}>
                          <Button variant="secondary" className="p-2">
                            <Edit size={16} />
                          </Button>
                        </Link>
                        <Button
                          variant="secondary"
                          className="p-2 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(badge)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Précédent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
